import type { ReactNode } from "react";
import { SkyCopyButton, SkyText } from "@skypointcloud/skypoint-ui";
import { ErrorState, LoadingRows } from "@/components/access/ScreenStates";
import { usePlatformResources } from "@/hooks/use-platform-resources";
import type { InstancePlatformResources } from "@shared/types/instances";

/**
 * What the platform provisioned for this instance, by name (owner, 2026-09-28: "a section to
 * show platform admin the key vault, storage account and tenant db details ... Only for
 * platform admin users. No need to show it even to tenant admins").
 *
 * The caller renders this only with `platform.instances.manage`, and the route checks the same
 * permission, so a tenant admin neither sees the section nor sends the request. It replaced the
 * "Platform storage" accordion every reader of the destination used to see: the audience of a
 * section decides where it goes, and nobody below a platform admin acts on these names.
 *
 * Identifiers only (SEC-17). Every value here is a name a platform admin looks up in the Azure
 * portal or in Postgres; none is a key, a SAS or a connection string, so each can be copied.
 * The Snowflake account is on the form above and is not repeated.
 */

/**
 * Why storage is on a destination page at all, and who changes it. It names no screen: no
 * screen edits storage (the platform route, `PUT /platform/instances/:id/binding`, is API-only).
 */
const STORAGE_OWNER_TEXT =
  "Imports stage this instance's files in this storage account, and the Snowflake stage reads them from there. It is the instance's own account, or the deployment's when the instance names none. Only a platform admin changes it, and not from this screen.";

interface Fact {
  label: string;
  /** The value to show and copy; null shows `missing` and offers no Copy. */
  value: string | null;
  missing: string;
  /** Said after the value, never copied: "(deployment default)" for a storage account. */
  qualifier?: string;
  testId: string;
}

/**
 * Round 6 walk W13: a new instance's binding names no account, and its imports stage in the
 * deployment's (`ADLS_ACCOUNT`). The value is that account, marked so a platform admin knows it
 * is shared, not this instance's own; Copy copies the bare name.
 */
const DEPLOYMENT_DEFAULT = "(deployment default)";

function factsOf(resources: InstancePlatformResources): Fact[] {
  return [
    {
      label: "Key Vault",
      value: resources.keyVaultName,
      missing:
        resources.secretStore === "local" ? "Local secret store (development)" : "Not known here",
      testId: "resources-keyVault",
    },
    {
      label: "Storage account",
      value: resources.storageAccount,
      missing: "Not set",
      ...(resources.storageAccountSource === "deployment" ? { qualifier: DEPLOYMENT_DEFAULT } : {}),
      testId: "storage-adlsAccount",
    },
    {
      label: "Staging container",
      value: resources.stagingContainer,
      missing: "Not set",
      testId: "storage-adlsContainerStaging",
    },
    {
      label: "Database server",
      value: resources.tenantDatabaseServer,
      missing: "Not known here",
      testId: "resources-databaseServer",
    },
    {
      label: "Tenant database",
      value: resources.tenantDatabaseName,
      missing: "Not known here",
      testId: "resources-tenantDatabase",
    },
    {
      label: "Instance schema",
      value: resources.instanceSchemaName,
      missing: "Not known here",
      testId: "resources-instanceSchema",
    },
    {
      label: "Job managed identity",
      value: resources.managedIdentityName,
      missing:
        resources.managedIdentityKind === "system-assigned" ? "System-assigned" : "Not known here",
      testId: "resources-managedIdentity",
    },
  ];
}

export function PlatformResources() {
  const { data, isPending, isError, error, refetch } = usePlatformResources(true);

  function renderResources(): ReactNode {
    if (isPending) {
      return <LoadingRows rows={3} columns={2} />;
    }
    if (isError) {
      return (
        <ErrorState
          title="The platform resources did not load"
          error={error}
          onRetry={() => void refetch()}
        />
      );
    }
    return (
      <>
        {/* Up to three columns of at least 15rem, one rule the library does not ship: its
              `.grid-cols-1` and `.sm:grid-cols-2` came after `lg:grid-cols-3` and held this at
              two columns (round 6, `libraryCascade.test.ts`). */}
        <dl className="grid grid-cols-[repeat(auto-fill,minmax(max(15rem,calc((100%_-_2rem)/3)),1fr))] gap-4">
          {factsOf(data).map((fact) => (
            <div key={fact.testId} className="min-w-0">
              <dt>
                <SkyText size={200} color="muted">
                  {fact.label}
                </SkyText>
              </dt>
              <dd className="flex min-w-0 items-center gap-1">
                <span data-testid={fact.testId} className="min-w-0 break-all">
                  <SkyText size={300} font="numeric" color={fact.value ? "default" : "muted"}>
                    {fact.value ?? fact.missing}
                  </SkyText>
                  {fact.value && fact.qualifier ? (
                    <SkyText size={200} color="muted">
                      {` ${fact.qualifier}`}
                    </SkyText>
                  ) : null}
                </span>
                {fact.value ? (
                  <SkyCopyButton
                    value={fact.value}
                    label={`Copy ${fact.label}`}
                    testId={`copy-${fact.testId}`}
                  />
                ) : null}
              </dd>
            </div>
          ))}
        </dl>
        <SkyText as="p" size={200} color="muted" block data-testid="platform-storage-owner">
          {STORAGE_OWNER_TEXT}
        </SkyText>
      </>
    );
  }

  return (
    <section
      aria-labelledby="platform-resources-heading"
      data-testid="platform-resources"
      className="space-y-4 rounded-lg border border-border bg-card p-4"
    >
      <div className="space-y-1">
        <SkyText as="h2" size={400} weight="semibold" block id="platform-resources-heading">
          Platform resources
        </SkyText>
        <SkyText as="p" size={200} color="muted" block data-testid="platform-resources-intro">
          What the platform provisioned for this instance. Shown to platform admins only.
        </SkyText>
      </div>

      {renderResources()}
    </section>
  );
}
